import {
  BlotterTable,
  Box,
  HStack,
  Icon,
  IconName,
  Text,
  useBlotterObservables,
  useBlotterTable,
  useStrategiesContext,
  VStack,
  type MixpanelEventSource,
  type UseBlotterTable,
} from '@talos/kyoko';
import type { GridOptions, RowClassParams } from 'ag-grid-community';
import {
  Orientation,
  SplitviewReact,
  type ISplitviewPanelProps,
  type SplitviewApi,
  type SplitviewReadyEvent,
} from 'dockview';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import type { OpenScenario } from '../../types';
import { ScenarioChart } from './ScenarioChart';

import { useScenarioColumns } from './useScenarioColumns';
import { buildScenarioComparisonRows, computeSharedMaxForBars, type ScenarioComparisonRow } from './utils';

const SCENARIOS_LIST_PANE_ID = 'scenarios-list-pane';
const SCENARIOS_CHART_PANE_ID = 'scenarios-chart-pane';
const SCENARIOS_LIST_COMPONENT = 'scenarios-list-component';
const SCENARIOS_CHART_COMPONENT = 'scenarios-chart-component';
const SCENARIOS_LIST_MIN_HEIGHT = 180;
const SCENARIOS_LIST_DEFAULT_HEIGHT = 320;

function ScenariosListPane({
  params,
}: ISplitviewPanelProps<{ blotterTable: UseBlotterTable<ScenarioComparisonRow>; scenarioCount: number }>) {
  if (!params.blotterTable) {
    return null;
  }

  return (
    <VStack alignItems="stretch" h="100%" overflow="hidden" data-testid="pre-trade-scenarios-list-pane">
      <Box flex={1} overflow="hidden">
        <BlotterTable {...params.blotterTable} />
      </Box>
      {params.scenarioCount <= 1 && (
        <HStack
          flex="0 0 auto"
          justifyContent="center"
          py="spacing8"
          borderBottom="solid 1px"
          borderColor="borderColorDivider"
        >
          <Icon icon={IconName.Play} size={12} color="colorTextMuted" />
          <Text size="fontSizeSm" fontWeight="fontWeightMedium" color="colorTextMuted" ml="spacing4">
            Use the order form to generate more scenarios.
          </Text>
        </HStack>
      )}
    </VStack>
  );
}

function ScenariosChartPane({
  params,
}: ISplitviewPanelProps<{ rows: ScenarioComparisonRow[]; colorMap: ReadonlyMap<string, string> }>) {
  if (!params.rows || !params.colorMap) {
    return null;
  }

  return (
    <Box h="100%" pt="spacing4" px="spacing12" pb="spacing12" data-testid="pre-trade-scenarios-chart-pane">
      <ScenarioChart rows={params.rows} colorMap={params.colorMap} />
    </Box>
  );
}

const SPLITVIEW_COMPONENTS: Record<string, React.FunctionComponent<ISplitviewPanelProps>> = {
  [SCENARIOS_LIST_COMPONENT]: ScenariosListPane as React.FunctionComponent<ISplitviewPanelProps>,
  [SCENARIOS_CHART_COMPONENT]: ScenariosChartPane as React.FunctionComponent<ISplitviewPanelProps>,
};

interface ScenariosTabProps {
  scenarios: OpenScenario[];
  activeScenarioIndex: number;
  colorMap: ReadonlyMap<string, string>;
  onShowDetails: (index: number) => void;
  onUseScenario: (index: number, source: MixpanelEventSource) => void;
}

export function ScenariosTab({
  scenarios,
  activeScenarioIndex,
  colorMap,
  onShowDetails,
  onUseScenario,
}: ScenariosTabProps) {
  const theme = useTheme();
  const { strategiesByName } = useStrategiesContext();
  const [splitviewApi, setSplitviewApi] = useState<SplitviewApi | undefined>(undefined);

  const rowData = useMemo(
    () => buildScenarioComparisonRows(scenarios, strategiesByName),
    [scenarios, strategiesByName]
  );

  const hasMultipleSymbols = useMemo(() => {
    return new Set(rowData.map(row => row.symbol)).size > 1;
  }, [rowData]);

  // Calculate shared max value for color bar scaling (highest value across all columns = 100%)
  const sharedMaxValue = useMemo(() => computeSharedMaxForBars(rowData), [rowData]);

  const { dataObservable, errorObservable } = useBlotterObservables({ data: rowData });

  const columns = useScenarioColumns({
    hasMultipleSymbols,
    sharedMaxValue,
    onShowDetails,
    onUseScenario,
    colorMap,
  });

  const gridOptions = useMemo<GridOptions<ScenarioComparisonRow>>(
    () => ({
      rowHeight: 48,
      headerHeight: 40,
      suppressRowClickSelection: true,
      suppressContextMenu: true,
      getRowStyle: (params: RowClassParams<ScenarioComparisonRow>) => {
        if (params.data?.index === activeScenarioIndex) {
          return { background: theme.colors.gray.main };
        }
        return undefined;
      },
    }),
    [activeScenarioIndex, theme.colors.gray.main]
  );

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable,
    rowID: 'rowID',
    columns,
    gridOptions,
  });

  // Set up the split view panel structure and sizing (runs once on mount)
  const handleSplitviewReady = useCallback((event: SplitviewReadyEvent) => {
    const { api } = event;
    setSplitviewApi(api);

    const containerHeight = api.height;
    const listHeight =
      !Number.isFinite(containerHeight) || (containerHeight ?? 0) <= 0
        ? SCENARIOS_LIST_DEFAULT_HEIGHT
        : Math.max(SCENARIOS_LIST_MIN_HEIGHT, Math.floor(containerHeight * 0.45));

    api.addPanel({
      id: SCENARIOS_LIST_PANE_ID,
      component: SCENARIOS_LIST_COMPONENT,
      size: listHeight,
      minimumSize: SCENARIOS_LIST_MIN_HEIGHT,
    });

    api.addPanel({
      id: SCENARIOS_CHART_PANE_ID,
      component: SCENARIOS_CHART_COMPONENT,
    });
  }, []);

  // Push latest data into the split view panes whenever it changes
  useEffect(() => {
    const api = splitviewApi;
    if (!api) {
      return;
    }

    const listPane = api.getPanel(SCENARIOS_LIST_PANE_ID);
    if (listPane) {
      listPane.api.updateParameters({ blotterTable, scenarioCount: scenarios.length });
    }

    const chartPane = api.getPanel(SCENARIOS_CHART_PANE_ID);
    if (chartPane) {
      chartPane.api.updateParameters({ rows: rowData, colorMap });
    }
  }, [splitviewApi, blotterTable, rowData, colorMap, scenarios.length]);

  return (
    <Box h="100%" overflow="hidden" data-testid="pre-trade-scenarios-splitview">
      <SplitviewReact
        className="dockview-theme-dark"
        orientation={Orientation.VERTICAL}
        components={SPLITVIEW_COMPONENTS}
        onReady={handleSplitviewReady}
      />
    </Box>
  );
}
